import { Ban, Check, CircleHelp } from 'lucide-react';
import { datumKompaktDe } from '@/lib/format';
import type { Lieferbarkeit } from '@/types/bestellung';

/**
 * Sagt am Treffer, ob es das Buch ueberhaupt noch gibt (#61).
 *
 * DER BEFUND
 *   Der ISBN-Lookup zeigte einen vergriffenen Titel genau wie einen
 *   lieferbaren. Am Tresen wurde dann eine Bestellung fuer ein Buch
 *   aufgenommen, das es nicht mehr gibt — auffallen konnte das erst
 *   Wochen spaeter beim Lieferanten.
 *
 * DREI ZUSTAENDE, DREI BEHANDLUNGEN
 *   Vergriffen faellt auf: das ist der Fall, der jemanden vor einer
 *   sinnlosen Bestellung bewahrt. Lieferbar bestaetigt leise. Und
 *   „unbekannt" steht ausdruecklich da, statt wegzubleiben — eine leere
 *   Stelle liest sich wie „alles in Ordnung", und genau diese stille
 *   Beruhigung war der Fehler, den das Register benennt.
 *
 * WARUM NICHT NUR DIE FARBE
 *   Coral (#993C1D) und Ink-soft (#5A5854) haben zueinander ein
 *   Kontrastverhaeltnis von 1,02:1 — sie sind fast exakt gleich hell.
 *   Wer die drei Faelle allein an der Farbe unterscheiden wollte, haette
 *   auf einem Tablet in der Sonne und fuer jeden mit Rotsehschwaeche gar
 *   nichts gesagt. Deshalb tragen sie verschiedenen TEXT, verschiedene
 *   SYMBOLE und im ernsten Fall einen Rahmen; die Farbe kommt obendrauf.
 *
 * DAS STAND-DATUM
 *   Verfuegbarkeit ist verderblich, der Cache haelt 30 Tage. Ein 28 Tage
 *   altes „lieferbar" ist eine Behauptung, keine Auskunft — ab sieben
 *   Tagen steht das Datum deshalb dabei. Darunter nicht: eine
 *   Datumsangabe an jeder Zeile ist Rauschen, und Rauschen ueberliest
 *   man mitsamt der Zeile, die etwas zu sagen hat.
 */

/** Ab wann das Stand-Datum mitgenannt wird. */
const STAND_TAGE = 7;

export function altAlsTage(standVom: string | null | undefined): number | null {
  if (!standVom) return null;
  // Zeitstempel aus der Datenbank sind UTC (siehe lib/format.ts).
  const d = new Date(standVom.includes('T') ? standVom : standVom.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return null;
  return (Date.now() - d.getTime()) / 86400000;
}

export function LieferbarkeitZeile({
  wert,
  standVom
}: {
  wert: Lieferbarkeit | undefined;
  standVom?: string | null;
}) {
  // Eine Antwort ohne das Feld (aeltere Fassung im Umlauf) behandeln wir
  // wie „unbekannt" — nicht wie „lieferbar".
  const zustand: Lieferbarkeit = wert ?? 'unbekannt';
  const alter = altAlsTage(standVom);
  const stand = alter !== null && alter >= STAND_TAGE ? ` · Stand ${datumKompaktDe(standVom)}` : '';

  if (zustand === 'nicht_lieferbar') {
    return (
      <p className="inline-flex items-center gap-1.5 rounded-md border border-coral px-2 py-1 text-sm font-medium text-coral">
        <Ban aria-hidden className="h-4 w-4 shrink-0" />
        Nicht mehr lieferbar
        {stand && <span className="font-normal text-xs">{stand}</span>}
      </p>
    );
  }

  if (zustand === 'lieferbar') {
    return (
      <p className="inline-flex items-center gap-1 text-xs text-ink-soft">
        <Check aria-hidden className="h-3.5 w-3.5 shrink-0 text-akzent" />
        Lieferbar{stand}
      </p>
    );
  }

  return (
    <p className="inline-flex items-center gap-1 text-xs text-ink-soft">
      <CircleHelp aria-hidden className="h-3.5 w-3.5 shrink-0" />
      Lieferbarkeit unbekannt{stand}
    </p>
  );
}
